import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Loader2, Briefcase, RotateCcw, Trash2, XCircle, CheckCircle2, Pencil, ExternalLink } from 'lucide-react';
import { FullPagination } from '../../components/admin/FullPagination';
import { ClientActionsMenu, ClientActionsMenuItem } from '../../components/admin/ClientActionsMenu';
import { ConfirmActionDialog } from '../../components/admin/ConfirmActionDialog';
import {
  careersService, JobPositionAdminRow, JobStatus, STATUS_LABELS, DEPARTMENT_LABELS,
} from '../../services/careersService';

const STATUS_STYLES: Record<JobStatus, string> = {
  aperta: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  chiusa: 'bg-secondary/60 text-muted-foreground border-border',
  bozza: 'bg-amber-50 text-amber-700 border-amber-200',
};

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('it-IT') : '—');

export default function AdminCareersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<JobPositionAdminRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const [q, setQ] = useState('');
  const [stato, setStato] = useState<JobStatus | ''>('');
  const [pubblicataDa, setPubblicataDa] = useState('');
  const [pubblicataA, setPubblicataA] = useState('');
  const [chiusaDa, setChiusaDa] = useState('');
  const [chiusaA, setChiusaA] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [confirm, setConfirm] = useState<{ row: JobPositionAdminRow; action: 'chiudi' | 'elimina' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await careersService.adminList({
        page, pageSize, q: q.trim() || undefined, stato: stato || undefined,
        pubblicataDa: pubblicataDa || undefined, pubblicataA: pubblicataA || undefined,
        chiusaDa: chiusaDa || undefined, chiusaA: chiusaA || undefined,
        includeDeleted,
      });
      setRows(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } finally {
      setLoading(false);
    }
  }, [page, q, stato, pubblicataDa, pubblicataA, chiusaDa, chiusaA, includeDeleted]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, stato, pubblicataDa, pubblicataA, chiusaDa, chiusaA, includeDeleted]);

  const resetFilters = () => {
    setQ(''); setStato(''); setPubblicataDa(''); setPubblicataA(''); setChiusaDa(''); setChiusaA(''); setIncludeDeleted(false);
  };

  const doAction = async (row: JobPositionAdminRow, action: 'chiudi' | 'riapri' | 'elimina' | 'ripristina') => {
    if (action === 'chiudi') await careersService.adminSetStato(row.id, 'chiusa');
    if (action === 'riapri') await careersService.adminSetStato(row.id, 'aperta');
    if (action === 'elimina') await careersService.adminDelete(row.id);
    if (action === 'ripristina') await careersService.adminRestore(row.id);
    load();
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-careers-page">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-[hsl(214,58%,41%)]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Carriere</h1>
            <p className="text-sm text-muted-foreground">Posizioni aperte pubblicate in &ldquo;Lavora con noi&rdquo;</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/carriere/nuova')}
          className="h-10 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[hsl(214,58%,46%)] transition-colors"
          data-testid="careers-new-btn"
        >
          <Plus className="w-4 h-4" /> Nuova posizione
        </button>
      </div>

      {/* Filtri */}
      <div className="rounded-xl border bg-white p-4 mb-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Cerca per titolo..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
              data-testid="careers-search-input"
            />
          </div>
          <select
            value={stato}
            onChange={e => setStato(e.target.value as JobStatus | '')}
            className="h-9 px-3 rounded-lg border text-sm bg-white"
            data-testid="careers-filter-stato"
          >
            <option value="">Tutti gli stati</option>
            <option value="aperta">Aperta</option>
            <option value="chiusa">Chiusa</option>
            <option value="bozza">Bozza</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={includeDeleted} onChange={e => setIncludeDeleted(e.target.checked)} data-testid="careers-filter-deleted" />
            Mostra eliminate
          </label>
          <button onClick={resetFilters} className="h-9 px-3 rounded-lg border text-sm hover:bg-secondary/60 transition-colors" data-testid="careers-filter-reset">
            Reset
          </button>
        </div>
        <div className="flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Pubblicata dal</span>
            <input type="date" value={pubblicataDa} onChange={e => setPubblicataDa(e.target.value)} className="h-8 px-2 rounded-lg border text-xs" data-testid="careers-filter-pub-da" />
            <span>al</span>
            <input type="date" value={pubblicataA} onChange={e => setPubblicataA(e.target.value)} className="h-8 px-2 rounded-lg border text-xs" data-testid="careers-filter-pub-a" />
          </div>
          <div className="flex items-center gap-2">
            <span>Chiusa dal</span>
            <input type="date" value={chiusaDa} onChange={e => setChiusaDa(e.target.value)} className="h-8 px-2 rounded-lg border text-xs" data-testid="careers-filter-chiusa-da" />
            <span>al</span>
            <input type="date" value={chiusaA} onChange={e => setChiusaA(e.target.value)} className="h-8 px-2 rounded-lg border text-xs" data-testid="careers-filter-chiusa-a" />
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="rounded-xl border bg-white overflow-hidden">
        <div className="grid grid-cols-[1fr_110px_120px_120px_130px_90px] text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-[hsl(var(--dtk-canvas))] border-b">
          <div className="px-3 py-2.5">Titolo</div>
          <div className="px-3 py-2.5">Stato</div>
          <div className="px-3 py-2.5">Pubblicazione</div>
          <div className="px-3 py-2.5">Chiusura</div>
          <div className="px-3 py-2.5">Cand. avviate</div>
          <div className="px-3 py-2.5 text-right">Azioni</div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="careers-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento...
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="careers-empty">
            Nessuna posizione trovata con i filtri correnti.
          </div>
        ) : rows.map(r => (
          <div
            key={r.id}
            className={`grid grid-cols-[1fr_110px_120px_120px_130px_90px] items-center border-b last:border-b-0 hover:bg-[hsl(var(--dtk-canvas))] transition-colors ${r.deletedAt ? 'opacity-60' : ''}`}
            data-testid={`careers-row-${r.id}`}
          >
            <button onClick={() => navigate(`/admin/carriere/${r.id}`)} className="px-3 py-3 text-left" data-testid={`careers-row-title-${r.id}`}>
              <div className="text-sm font-medium">{r.titolo}</div>
              <div className="text-xs text-muted-foreground">
                {DEPARTMENT_LABELS[r.area]} · {r.slug}
                {r.deletedAt && <span className="ml-1 text-red-600 font-medium">· eliminata</span>}
                {r.scaduta && r.stato === 'aperta' && <span className="ml-1 text-amber-700 font-medium">· scaduta</span>}
              </div>
            </button>
            <div className="px-3 py-3">
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border ${STATUS_STYLES[r.stato]}`} data-testid={`careers-status-${r.id}`}>
                {STATUS_LABELS[r.stato]}
              </span>
            </div>
            <div className="px-3 py-3 text-sm">{fmtDate(r.dataPubblicazione)}</div>
            <div className="px-3 py-3 text-sm">{fmtDate(r.dataChiusura)}</div>
            <div className="px-3 py-3 text-sm font-medium" data-testid={`careers-clicks-${r.id}`}>{r.clickCandidature}</div>
            <div className="px-3 py-3 flex items-center justify-end">
              <ClientActionsMenu testId={`careers-actions-${r.id}`}>
                <ClientActionsMenuItem to={`/admin/carriere/${r.id}`} testId={`careers-open-${r.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Apri / Modifica
                </ClientActionsMenuItem>
                <ClientActionsMenuItem to={`/azienda/lavora-con-noi/${r.slug}`} testId={`careers-view-public-${r.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" /> Vedi pagina pubblica
                </ClientActionsMenuItem>
                {r.deletedAt ? (
                  <ClientActionsMenuItem onClick={() => doAction(r, 'ripristina')} testId={`careers-restore-${r.id}`}>
                    <RotateCcw className="w-3.5 h-3.5" /> Ripristina
                  </ClientActionsMenuItem>
                ) : (
                  <>
                    {r.stato === 'chiusa' && (
                      <ClientActionsMenuItem onClick={() => doAction(r, 'riapri')} testId={`careers-reopen-${r.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Riapri posizione
                      </ClientActionsMenuItem>
                    )}
                    {r.stato !== 'chiusa' && (
                      <ClientActionsMenuItem onClick={() => setConfirm({ row: r, action: 'chiudi' })} testId={`careers-close-${r.id}`}>
                        <XCircle className="w-3.5 h-3.5 text-amber-600" /> Chiudi posizione
                      </ClientActionsMenuItem>
                    )}
                    <ClientActionsMenuItem onClick={() => setConfirm({ row: r, action: 'elimina' })} testId={`careers-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-600" /> <span className="text-red-600">Elimina</span>
                    </ClientActionsMenuItem>
                  </>
                )}
              </ClientActionsMenu>
            </div>
          </div>
        ))}
      </div>

      <FullPagination page={page} pages={pages} total={total} pageSize={pageSize} onPageChange={setPage} testIdPrefix="careers-pagination" />

      <ConfirmActionDialog
        open={!!confirm}
        onOpenChange={o => { if (!o) setConfirm(null); }}
        variant={confirm?.action === 'elimina' ? 'destructive' : 'warning'}
        title={confirm?.action === 'elimina' ? 'Eliminare la posizione?' : 'Chiudere la posizione?'}
        description={confirm?.action === 'elimina'
          ? `"${confirm?.row.titolo}" verrà nascosta da tutte le viste. Lo storico resta conservato e puoi ripristinarla.`
          : `"${confirm?.row.titolo}" non accetterà più candidature. Il contenuto e lo storico restano conservati e puoi riaprirla.`}
        confirmLabel={confirm?.action === 'elimina' ? 'Elimina' : 'Chiudi posizione'}
        onConfirm={() => { if (confirm) doAction(confirm.row, confirm.action === 'elimina' ? 'elimina' : 'chiudi'); setConfirm(null); }}
      />
    </div>
  );
}
